import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import useFetch from 'use-http';
import CardSection from 'src/components/common/CardSection';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import {
    MatchTier,
    recordingLabel,
    IThirdPartyLeaseDuplicateCluster,
    IThirdPartyLeaseListItem,
    IThirdPartyLeasePage,
} from 'src/models/thirdPartyLease/ThirdPartyLease';
import DuplicateClusterCard from './DuplicateClusterCard';

import './ThirdPartyLeases.scss';

const PAGE_SIZE = 25;

type TabKey = 'all' | 'duplicates';

const formatDate = (value?: string) => (value ? moment(value).format('MM/DD/YYYY') : 'N/A');

/* Lists the third-party (non-generated) contracts in a project - every agreement type, not just
   oil and gas - and groups the ones that look like the same contract entered more than once.
   Read-only for now; merging is a later phase. */
const ThirdPartyLeasesPage = () => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { get, response } = useFetch();

    const [tab, setTab] = useState<TabKey>('duplicates');
    const [search, setSearch] = useState('');
    const [tierFilter, setTierFilter] = useState<MatchTier | ''>('');
    const [typeFilter, setTypeFilter] = useState('');
    const [agreementTypes, setAgreementTypes] = useState<Array<string>>([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>(undefined);

    const [contracts, setContracts] = useState<IThirdPartyLeasePage<IThirdPartyLeaseListItem>>(undefined);
    const [clusters, setClusters] = useState<IThirdPartyLeasePage<IThirdPartyLeaseDuplicateCluster>>(undefined);

    const basePath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/third-party-leases`;

    const load = useCallback(
        async (currentTab: TabKey, currentSearch: string, currentTier: MatchTier | '', currentType: string, page: number) => {
            if (!appContext.clientNumber || !appContext.projectNumber) {
                return;
            }

            setIsLoading(true);
            setError(undefined);

            const params = new URLSearchParams();
            params.append('pageNumber', String(page));
            params.append('pageSize', String(PAGE_SIZE));

            if (currentType.length > 0) {
                params.append('agreementType', currentType);
            }

            let path: string;

            if (currentTab === 'duplicates') {
                if (currentTier.length > 0) {
                    params.append('matchTier', currentTier);
                }
                path = `${basePath}/duplicates?${params.toString()}`;
            } else {
                if (currentSearch.length > 0) {
                    params.append('search', currentSearch);
                }
                path = `${basePath}?${params.toString()}`;
            }

            const result = await get(path);

            if (response.ok) {
                if (currentTab === 'duplicates') {
                    setClusters(result);
                } else {
                    setContracts(result);
                }
            } else {
                setError('Could not load third-party contracts.');
            }

            setIsLoading(false);
        },
        [appContext.clientNumber, appContext.projectNumber, basePath, get, response]
    );

    /* Debounced so typing in the search box does not fire a request per keystroke - the staged
       query behind this endpoint is cheap but not free. */
    const debouncedSearch = useCallback(
        _.debounce((value: string) => {
            setPageNumber(1);
            setSearch(value);
        }, 500),
        []
    );

    useEffect(() => {
        const loadTypes = async () => {
            if (!appContext.clientNumber || !appContext.projectNumber) {
                return;
            }

            const result = await get(`${basePath}/agreement-types`);

            if (response.ok && Array.isArray(result)) {
                setAgreementTypes(result);
            }
        };

        loadTypes();
    }, [appContext.clientNumber, appContext.projectNumber]);

    useEffect(() => {
        load(tab, search, tierFilter, typeFilter, pageNumber);
    }, [tab, search, tierFilter, typeFilter, pageNumber]);

    const current = tab === 'duplicates' ? clusters : contracts;
    const totalPages = current ? Math.max(1, Math.ceil(current.totalRecords / PAGE_SIZE)) : 1;

    const switchTab = (next: TabKey) => {
        setTab(next);
        setPageNumber(1);
    };

    const renderToolbar = () => (
        <div className="tpl-toolbar">
            <div className="tpl-tabs">
                <button
                    type="button"
                    className={`tpl-tab ${tab === 'duplicates' ? 'is-active' : ''}`}
                    onClick={() => switchTab('duplicates')}
                >
                    Possible Duplicates
                </button>
                <button
                    type="button"
                    className={`tpl-tab ${tab === 'all' ? 'is-active' : ''}`}
                    onClick={() => switchTab('all')}
                >
                    All 3rd-Party Contracts
                </button>
            </div>

            <select
                className="tpl-select"
                value={typeFilter}
                onChange={(e) => {
                    setPageNumber(1);
                    setTypeFilter(e.target.value);
                }}
            >
                <option value="">All contract types</option>
                {agreementTypes.map((type) => (
                    <option key={type} value={type}>
                        {type}
                    </option>
                ))}
            </select>

            {tab === 'all' && (
                <input
                    className="tpl-search"
                    type="text"
                    placeholder="Search lessor, lessee, book/page or instrument #"
                    onChange={(e) => debouncedSearch(e.target.value)}
                />
            )}

            {tab === 'duplicates' && (
                <select
                    className="tpl-select"
                    value={tierFilter}
                    onChange={(e) => {
                        setPageNumber(1);
                        setTierFilter(e.target.value as MatchTier | '');
                    }}
                >
                    <option value="">All confidence levels</option>
                    <option value="High">High — recording info + lessor + date</option>
                    <option value="Low">Low — no recording info</option>
                </select>
            )}

            {current && (
                <span className="tpl-summary">
                    {tab === 'duplicates'
                        ? `${current.totalRecords} duplicate group${current.totalRecords === 1 ? '' : 's'}`
                        : `${current.totalRecords} contract${current.totalRecords === 1 ? '' : 's'}`}
                </span>
            )}
        </div>
    );

    const renderContractList = () => (
        <div>
            <div className="tpl-row tpl-head">
                <div className="tpl-col-type">Type</div>
                <div className="tpl-col-recording">Vol/Page | Inst#</div>
                <div className="tpl-col-date">Date</div>
                <div className="tpl-col-lessor">Lessor / Grantor</div>
                <div className="tpl-col-lessee">Lessee / Grantee</div>
                <div className="tpl-col-count">Holds</div>
                <div className="tpl-col-dupe">Look-alikes</div>
            </div>

            {contracts?.items?.map((contract) => (
                <div key={`${contract.agreementType}-${contract.contractId}`} className="tpl-row">
                    <div className="tpl-col-type">{contract.agreementType}</div>
                    <div className="tpl-col-recording">{recordingLabel(contract)}</div>
                    <div className="tpl-col-date">{formatDate(contract.contractDate)}</div>
                    <div className="tpl-col-lessor">{contract.originalLessor || 'N/A'}</div>
                    <div className="tpl-col-lessee">{contract.originalLessee || 'N/A'}</div>
                    <div className="tpl-col-count">{contract.holdCount}</div>
                    <div className="tpl-col-dupe">{contract.duplicateCount > 0 ? contract.duplicateCount : '—'}</div>
                </div>
            ))}

            {contracts?.items?.length === 0 && <div className="tpl-empty">No third-party contracts found.</div>}
        </div>
    );

    const renderClusters = () => (
        <div>
            {clusters?.items?.map((cluster) => (
                <DuplicateClusterCard key={cluster.clusterKey} cluster={cluster} />
            ))}

            {clusters?.items?.length === 0 && (
                <div className="tpl-empty">No duplicate third-party contracts found in this project.</div>
            )}
        </div>
    );

    return (
        <CardSection title="3rd-Party Contracts">
            {renderToolbar()}

            {error && <div className="tpl-empty">{error}</div>}

            {isLoading && <div className="tpl-empty">---Loading---</div>}

            {!isLoading && !error && (tab === 'duplicates' ? renderClusters() : renderContractList())}

            {!isLoading && current && current.totalRecords > PAGE_SIZE && (
                <div className="tpl-paging">
                    <button type="button" disabled={pageNumber <= 1} onClick={() => setPageNumber(pageNumber - 1)}>
                        Previous
                    </button>
                    <span>
                        Page {pageNumber} of {totalPages}
                    </span>
                    <button
                        type="button"
                        disabled={pageNumber >= totalPages}
                        onClick={() => setPageNumber(pageNumber + 1)}
                    >
                        Next
                    </button>
                </div>
            )}
        </CardSection>
    );
};

export default ThirdPartyLeasesPage;



// WEBPACK FOOTER //
// ./src/components/project/sections/ThirdPartyLeases/ThirdPartyLeasesPage.tsx